/**
 * Page Action Registry
 *
 * Zustand store that holds page actions registered by plugins.
 * Page actions are contextual operations (Share, Delete, Pin, etc.)
 * that appear on toolbars, FABs, command palettes, and context menus.
 *
 * Plugins register actions declaratively; the UI layer queries the
 * registry for actions matching the current page/entity/device context.
 *
 * No domain logic lives here — the registry is pure infrastructure.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  PageAction,
  PageActionContext,
  PageActionPlacement,
  DeviceType,
} from '../../types/page-actions';

// ── Path matching helper ────────────────────────────────────────

/**
 * Match a concrete page path against a pattern with glob support.
 *
 * Patterns:
 *   "/entities"       — exact match
 *   "/entities/*"     — matches "/entities/abc", "/entities/xyz"
 *   "/entities/**"    — matches "/entities/abc", "/entities/abc/def"
 *
 * If no pattern is specified the action matches all pages.
 */
function matchPageId(pagePath: string, pattern: string): boolean {
  if (pattern === pagePath) return true;

  if (pattern.endsWith('/**')) {
    const prefix = pattern.slice(0, -3);
    return pagePath === prefix || pagePath.startsWith(prefix + '/');
  }

  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2);
    // Must start with prefix/ and have exactly one more segment
    if (!pagePath.startsWith(prefix + '/')) return false;
    const remainder = pagePath.slice(prefix.length + 1);
    return remainder.length > 0 && !remainder.includes('/');
  }

  return false;
}

// ── Store types ─────────────────────────────────────────────────

interface PageActionRegistryState {
  /** All registered page actions, keyed by action ID */
  actions: Map<string, PageAction>;

  /** Track which plugins own which action IDs */
  pluginActions: Map<string, Set<string>>;

  /** Register a single page action */
  registerAction: (action: PageAction) => void;

  /** Register multiple page actions at once */
  registerActions: (actions: PageAction[]) => void;

  /** Unregister a single page action by ID */
  unregisterAction: (actionId: string) => void;

  /** Unregister all page actions from a specific plugin */
  unregisterPlugin: (pluginId: string) => void;

  /**
   * Get actions matching the given runtime context.
   *
   * Filtering rules (all must pass):
   *   1. entityType — if the action declares one, it must match the context
   *   2. pageIds   — if the action declares page patterns, at least one must match
   *   3. deviceTypes — if the action declares device constraints, current device must match
   *   4. placement — if specified, only actions with that placement
   *
   * Results are sorted by effective priority (lower first).
   */
  getActionsForContext: (
    context: PageActionContext,
    placement?: PageActionPlacement,
  ) => PageAction[];

  /** Get all actions for a specific plugin */
  getActionsForPlugin: (pluginId: string) => PageAction[];

  /** Get a single action by ID */
  getAction: (actionId: string) => PageAction | undefined;

  /** Get all registered actions as an array */
  getAllActions: () => PageAction[];

  /** Clear all registrations */
  clear: () => void;
}

// ── Store ───────────────────────────────────────────────────────

export const usePageActionRegistry = create<PageActionRegistryState>()(
  devtools(
    (set, get) => ({
      actions: new Map(),
      pluginActions: new Map(),

      registerAction: (action: PageAction) => {
        set((state) => {
          const newActions = new Map(state.actions);
          newActions.set(action.id, action);

          const newPluginActions = new Map(state.pluginActions);
          const existing = newPluginActions.get(action.pluginId) ?? new Set();
          existing.add(action.id);
          newPluginActions.set(action.pluginId, existing);

          return { actions: newActions, pluginActions: newPluginActions };
        });
      },

      registerActions: (actions: PageAction[]) => {
        set((state) => {
          const newActions = new Map(state.actions);
          const newPluginActions = new Map(state.pluginActions);

          for (const action of actions) {
            newActions.set(action.id, action);
            const existing = newPluginActions.get(action.pluginId) ?? new Set();
            existing.add(action.id);
            newPluginActions.set(action.pluginId, existing);
          }

          return { actions: newActions, pluginActions: newPluginActions };
        });
      },

      unregisterAction: (actionId: string) => {
        set((state) => {
          const action = state.actions.get(actionId);
          if (!action) return state;

          const newActions = new Map(state.actions);
          newActions.delete(actionId);

          const newPluginActions = new Map(state.pluginActions);
          const pluginSet = newPluginActions.get(action.pluginId);
          if (pluginSet) {
            pluginSet.delete(actionId);
            if (pluginSet.size === 0) {
              newPluginActions.delete(action.pluginId);
            }
          }

          return { actions: newActions, pluginActions: newPluginActions };
        });
      },

      unregisterPlugin: (pluginId: string) => {
        set((state) => {
          const actionIds = state.pluginActions.get(pluginId);
          if (!actionIds || actionIds.size === 0) return state;

          const newActions = new Map(state.actions);
          for (const id of actionIds) {
            newActions.delete(id);
          }

          const newPluginActions = new Map(state.pluginActions);
          newPluginActions.delete(pluginId);

          return { actions: newActions, pluginActions: newPluginActions };
        });
      },

      getActionsForContext: (
        context: PageActionContext,
        placement?: PageActionPlacement,
      ): PageAction[] => {
        const results: PageAction[] = [];

        for (const action of get().actions.values()) {
          // 1. Entity type filter
          if (action.entityType && context.entityType && action.entityType !== context.entityType) {
            continue;
          }

          // 2. Page ID filter (glob matching)
          if (action.pageIds && action.pageIds.length > 0) {
            const pageMatches = action.pageIds.some((pattern) =>
              matchPageId(context.pageId, pattern),
            );
            if (!pageMatches) continue;
          }

          // 3. Device type filter
          if (action.deviceTypes && action.deviceTypes.length > 0) {
            if (!action.deviceTypes.includes(context.deviceType)) continue;
          }

          // 4. Placement filter
          if (placement) {
            const actionPlacements = action.placements ?? ['toolbar', 'command-palette'];
            // On mobile, check if mobile config overrides placement
            const effectivePlacements = context.isMobile && action.mobile?.placement
              ? [action.mobile.placement]
              : actionPlacements;
            if (!effectivePlacements.includes(placement)) continue;
          }

          // 5. Mobile visibility
          if (context.isMobile && action.mobile?.visible === false) {
            continue;
          }

          results.push(action);
        }

        // Sort by effective priority (respect mobile priority override)
        return results.sort((a, b) => {
          const aPriority = context.isMobile && a.mobile?.priority !== undefined
            ? a.mobile.priority
            : (a.priority ?? 50);
          const bPriority = context.isMobile && b.mobile?.priority !== undefined
            ? b.mobile.priority
            : (b.priority ?? 50);
          return aPriority - bPriority;
        });
      },

      getActionsForPlugin: (pluginId: string): PageAction[] => {
        const actionIds = get().pluginActions.get(pluginId);
        if (!actionIds) return [];
        const results: PageAction[] = [];
        for (const id of actionIds) {
          const action = get().actions.get(id);
          if (action) results.push(action);
        }
        return results.sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));
      },

      getAction: (actionId: string) => get().actions.get(actionId),

      getAllActions: () =>
        Array.from(get().actions.values()).sort(
          (a, b) => (a.priority ?? 50) - (b.priority ?? 50),
        ),

      clear: () => {
        set({ actions: new Map(), pluginActions: new Map() });
      },
    }),
    { name: 'page-action-registry' },
  ),
);

// ── Convenience hooks ───────────────────────────────────────────

/**
 * Hook to get page actions for the current context.
 * Re-renders when the action registry changes.
 *
 * Memoized: the returned array is referentially stable unless
 * the registry contents, context, or placement change. This
 * prevents re-render storms in PageActionProvider subtrees.
 */
export function usePageActions(
  context: PageActionContext,
  placement?: PageActionPlacement,
): PageAction[] {
  const actions = usePageActionRegistry((s) => s.actions);

  // useMemo ensures we only recompute when the registry or context changes
  return useMemo(
    () => usePageActionRegistry.getState().getActionsForContext(context, placement),
    [actions, context.pageId, context.entityType, context.deviceType, context.isMobile, placement],
  );
}

/**
 * Hook to get the mobile-effective icon for a page action.
 * Respects mobile.icon override when the context is mobile.
 */
export function getMobileEffectiveIcon(action: PageAction, isMobile: boolean): string | undefined {
  return isMobile && action.mobile?.icon ? action.mobile.icon : action.icon;
}

/**
 * Hook to get the mobile-effective label for a page action.
 * Respects mobile.label override when the context is mobile.
 */
export function getMobileEffectiveLabel(action: PageAction, isMobile: boolean): string {
  return isMobile && action.mobile?.label ? action.mobile.label : action.label;
}
